/* eslint-disable no-unused-vars */
// ============================================
// @narbodica/auth — utils/password.js
// ============================================
// Password strength scoring and breach detection.
// HIBP k-anonymity model — password never sent in full.
// ============================================

/**
 * Scores password strength.
 * Returns { score: 0-4, label, color, pct }
 */
export function checkPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: '#52525b', pct: 0 };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  // Normalise to 0-4
  score = Math.min(4, score);

  const levels = [
    { label: 'Too weak',  color: '#ef4444', pct: 15 },
    { label: 'Weak',      color: '#f97316', pct: 35 },
    { label: 'Fair',      color: '#eab308', pct: 60 },
    { label: 'Good',      color: '#22c55e', pct: 80 },
    { label: 'Strong',    color: '#10b981', pct: 100 },
  ];

  return { score, ...levels[score] };
}

/**
 * Checks password against Have I Been Pwned using k-anonymity.
 * Returns true if password has been breached.
 * Never sends the full password — only the first 5 chars of the SHA-1 hash.
 */
export async function checkPasswordBreach(password) {
  try {
    // SHA-1 hash the password
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();

    const prefix = hashHex.slice(0, 5);
    const suffix = hashHex.slice(5);

    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' }
    });
    if (!res.ok) return false;

    const text = await res.text();
    const lines = text.split('\n');
    for (const line of lines) {
      const [hash] = line.split(':');
      if (hash.trim().toUpperCase() === suffix) return true;
    }
    return false;
  } catch {
    // Never block login on breach check failure
    return false;
  }
}
